import { useEffect, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ConfettiIcon } from '@phosphor-icons/react';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';
import Step from 'src/components/ui/Step';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import RightPanelShell from 'src/components/layouts/RightPanelShell';

export default function DataLibraryGuide() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();

  const [steps, setSteps] = useState<{
    tour: 'incomplete' | 'loading' | 'complete';
    upload: 'incomplete' | 'loading' | 'complete';
    summary: 'incomplete' | 'loading' | 'complete';
  }>({
    tour: 'loading',
    upload: 'incomplete',
    summary: 'incomplete'
  });

  // Hydrate from localStorage on mount so completion persists across navigations
  useEffect(() => {
    try {
      const tourDone = localStorage.getItem('innex-data-library-tour-completed') === 'true';
      const uploadDone = localStorage.getItem('innex-data-library-upload-completed') === 'true';
      const summaryDone = localStorage.getItem('innex-data-library-summary-validated') === 'true';
      setSteps({
        tour: tourDone ? 'complete' : 'loading',
        upload: uploadDone ? 'complete' : tourDone ? 'loading' : 'incomplete',
        summary: summaryDone ? 'complete' : uploadDone ? 'loading' : 'incomplete'
      });
    } catch {}
  }, []);

  const setActiveStep = (key: 'tour' | 'upload' | 'summary') => {
    setSteps((prev) => {
      if (prev[key] === 'loading') return prev;
      const next = { ...prev } as typeof prev;
      if (next.tour === 'loading') next.tour = 'incomplete';
      if (next.upload === 'loading') next.upload = 'incomplete';
      if (next.summary === 'loading') next.summary = 'incomplete';
      next[key] = 'loading';
      return next;
    });
  };

  // Ensure the Data Library admin tab is open
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname !== '/admin' || params.get('tab') !== 'data-library') {
      navigate('/admin?tab=data-library');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance: tour completion
  useEffect(() => {
    if (steps.tour !== 'loading') return;
    const id = setInterval(() => {
      const done = localStorage.getItem('innex-data-library-tour-completed') === 'true';
      if (done) {
        setSteps((s) => ({ ...s, tour: 'complete', upload: s.upload === 'incomplete' ? 'loading' : s.upload }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.tour]);

  // Auto-advance: upload completion
  useEffect(() => {
    if (steps.upload !== 'loading') return;
    const id = setInterval(() => {
      const done = localStorage.getItem('innex-data-library-upload-completed') === 'true';
      if (done) {
        setSteps((s) => ({ ...s, upload: 'complete', summary: s.summary === 'incomplete' ? 'loading' : s.summary }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.upload]);

  useEffect(() => {
    if (steps.summary !== 'loading') return;
    const id = setInterval(() => {
      const done = localStorage.getItem('innex-data-library-summary-validated') === 'true';
      if (done) {
        setSteps((s) => ({ ...s, summary: 'complete' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.summary]);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Data Library</h2>
    </div>
  );

  const allComplete = steps.tour === 'complete' && steps.upload === 'complete' && steps.summary === 'complete';

  const skipStep = () => {
    setSteps((s) => {
      const order = ['tour', 'upload', 'summary'] as const;
      const loadingIdx = order.findIndex((k) => s[k] === 'loading');
      const nextState = { ...s } as typeof s;
      if (loadingIdx >= 0) {
        nextState[order[loadingIdx]] = 'incomplete';
        const relNextIdx = order.slice(loadingIdx + 1).findIndex((k) => s[k] === 'incomplete');
        if (relNextIdx >= 0) {
          const idx = loadingIdx + 1 + relNextIdx;
          nextState[order[idx]] = 'loading';
          return nextState;
        }
        const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
        if (firstIncompleteIdx >= 0) {
          nextState[order[firstIncompleteIdx]] = 'loading';
          return nextState;
        }
        return s;
      }
      const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
      if (firstIncompleteIdx >= 0) {
        nextState[order[firstIncompleteIdx]] = 'loading';
        return nextState;
      }
      return s;
    });
  };

  const footer = allComplete ? (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-dataLibrary-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  ) : (
    <Button variant="outline" size="md" className="w-full" onClick={skipStep}>
      Skip Step
    </Button>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-2">
        {(() => {
          return (
            <>
              <Step
                title="Complete Tour"
                state={steps.tour}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-data-library-tour-completed');
                  } catch {}
                  setActiveStep('tour');
                }}
              >
                {steps.tour === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">Start our short guided tour of the Data Library.</div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          window.dispatchEvent(new Event('start-data-library-tour'));
                        } catch {}
                      }}
                    >
                      Start tour
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Upload a document"
                state={steps.upload}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-data-library-upload-completed');
                  } catch {}
                  setActiveStep('upload');
                }}
              >
                {steps.upload === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      Click "Upload Documents" on the Data Library page, select a file, and complete the upload. After
                      it finishes processing, press the button below.
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          localStorage.setItem('innex-data-library-upload-completed', 'true');
                        } catch {}
                        setSteps((s) => ({ ...s, upload: 'complete', summary: 'loading' }));
                      }}
                    >
                      I uploaded a document
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Validate the summary"
                state={steps.summary}
                isLast={!allComplete}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-data-library-summary-validated');
                  } catch {}
                  setActiveStep('summary');
                }}
              >
                {steps.summary === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">Find your document:</div>
                    <ol className="list-decimal pl-5 text-sm text-content-subtle space-y-1 ">
                      <li>
                        Look for the{' '}
                        <strong>
                          <span className="whitespace-nowrap">document in the first row</span>
                        </strong>{' '}
                        or sort by most recently uploaded.
                      </li>
                      <li>
                        Find the three dots on the row,{' '}
                        <strong>
                          <span className="whitespace-nowrap">click on Edit</span>
                        </strong>{' '}
                        and then review the summary. Make any edits.
                      </li>
                    </ol>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          localStorage.setItem('innex-data-library-summary-validated', 'true');
                        } catch {}
                        setSteps((s) => ({ ...s, summary: 'complete' }));
                      }}
                    >
                      I validated the summary
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              {allComplete && (
                <Step
                  title="Congrats, you're all set!"
                  state="loading"
                  isLast={true}
                  icon={<ConfettiIcon size={28} className="text-secondary-blue" />}
                >
                  <div className="text-sm text-content-subtle">
                    Great work! You've added a document to your Data Library and validated its summary.
                  </div>
                </Step>
              )}
            </>
          );
        })()}
      </div>
    </RightPanelShell>
  );
}
